import React, { useState } from 'react'
import {
  Typography,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  useTheme,
} from '@mui/material'
import { FiberManualRecordSharp, InfoOutlined } from '@mui/icons-material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { alpha } from '@mui/material/styles'
import { TOUCH_MIN } from 'core/theme.tokens'

/** Page title scale (~80% of the previous sizes). */
export const PAGE_TITLE_FONT = { xs: '0.9rem', sm: '1.08rem' } as const
/** In-page tab label scale (~80% of page title). */
export const PAGE_TAB_FONT = { xs: '0.72rem', sm: '0.875rem' } as const

interface PageTitleProps {
  title: string
  /** Secondary segment after a dot (e.g. selected CRM tab). Renders smaller than the title. */
  section?: string
  subtitle?: string
  bulletPoints?: string[]
  action?: React.ReactNode
}

export const PageTitle = (props: PageTitleProps) => {
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'))
  const [infoOpen, setInfoOpen] = useState(false)
  const hasInfo = Boolean(props.subtitle || (props.bulletPoints && props.bulletPoints.length > 0))
  const aboutLabel = props.section ? `${props.title} • ${props.section}` : props.title

  const infoBody = (
    <Box>
      {props.subtitle ? (
        <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
          {props.subtitle}
        </Typography>
      ) : null}
      {props.bulletPoints && props.bulletPoints.length > 0 ? (
        <List disablePadding sx={{ mt: props.subtitle ? 1 : 0 }}>
          {props.bulletPoints.map((point, index) => (
            <ListItem key={index} sx={{ py: 0.5, pl: 0.5, alignItems: 'flex-start' }}>
              <FiberManualRecordSharp
                fontSize='inherit'
                sx={{
                  color: theme.custom.chrome,
                  mr: 1,
                  mt: 0.75,
                  fontSize: 8,
                  flexShrink: 0,
                }}
              />
              <Typography variant='body2' color='text.secondary'>
                {point}
              </Typography>
            </ListItem>
          ))}
        </List>
      ) : null}
    </Box>
  )

  const infoButton = (
    <IconButton
      size='small'
      aria-label={`About ${aboutLabel}`}
      onClick={
        isPhone || (props.bulletPoints && props.bulletPoints.length > 0)
          ? () => setInfoOpen(true)
          : undefined
      }
      sx={{
        color: 'text.secondary',
        p: 0.25,
        minWidth: { xs: TOUCH_MIN, sm: 28 },
        minHeight: { xs: TOUCH_MIN, sm: 28 },
        flexShrink: 0,
        '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.06), color: 'primary.main' },
      }}
    >
      <InfoOutlined sx={{ fontSize: { xs: 18, sm: 16 } }} />
    </IconButton>
  )

  return (
    <Box sx={{ mb: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          columnGap: 1,
          rowGap: 0.75,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            minWidth: 0,
            flex: '1 1 auto',
          }}
        >
          <Typography
            variant='h4'
            component='h1'
            sx={{
              fontSize: PAGE_TITLE_FONT,
              lineHeight: 1.25,
              fontWeight: 600,
              color: 'text.primary',
              letterSpacing: '-0.01em',
              minWidth: 0,
              display: 'inline-flex',
              alignItems: 'baseline',
              flexWrap: 'wrap',
              columnGap: 0.75,
              rowGap: 0.15,
            }}
          >
            <Box component='span'>{props.title}</Box>
            {props.section ? (
              <>
                <Box
                  component='span'
                  aria-hidden
                  sx={{ color: 'text.secondary', fontWeight: 500, opacity: 0.7 }}
                >
                  •
                </Box>
                <Box
                  component='span'
                  sx={{
                    fontSize: PAGE_TAB_FONT,
                    fontWeight: 600,
                    color: 'text.secondary',
                    letterSpacing: 0,
                  }}
                >
                  {props.section}
                </Box>
              </>
            ) : null}
          </Typography>
          {hasInfo ? (
            isPhone || (props.bulletPoints && props.bulletPoints.length > 0) ? (
              infoButton
            ) : (
              <Tooltip
                title={props.subtitle || ''}
                placement='bottom-start'
                arrow
                enterTouchDelay={0}
                slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
              >
                <Box component='span' sx={{ display: 'inline-flex' }}>
                  {infoButton}
                </Box>
              </Tooltip>
            )
          ) : null}
        </Box>
        {props.action ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'flex-end', sm: 'flex-end' },
              flexWrap: 'wrap',
              gap: 0.75,
              ml: 'auto',
              flex: { xs: '1 1 auto', sm: '0 1 auto' },
              minWidth: 0,
              maxWidth: { xs: '100%', sm: 'none' },
              '& .MuiButton-root, & .MuiToggleButton-root': {
                minHeight: { xs: TOUCH_MIN, sm: 32 },
                px: { xs: 1.25, sm: 1.5 },
                fontSize: { xs: '0.8125rem', sm: '0.8125rem' },
              },
            }}
          >
            {props.action}
          </Box>
        ) : null}
      </Box>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} fullWidth maxWidth='xs'>
        <DialogTitle sx={{ fontWeight: 600, pr: 2 }}>{aboutLabel}</DialogTitle>
        <DialogContent>{infoBody}</DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} sx={{ minHeight: TOUCH_MIN }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
